// "Confirm the format" / recover screen (Phase 1b, cloud).
//
// Mirrors the DESKTOP confirm screen (app/src/components/SourcePane.tsx
// ReviewPane) for visual parity, but built from the cloud wizard's own flat
// chrome — step-title / step-lede + a divider-separated FILE block (no nested
// cards-in-cards). Two states share this surface:
//
//   confirm — detection wasn't confident (low score, or two formats too close).
//             The detector's guess is shown bold with an italic "Aurora's
//             guess" tag; the runner-up / alternatives are selectable. The user
//             confirms or overrides, then Continue parses. Heading "Confirm the
//             format."
//   recover — the machine couldn't read it: nothing scored, OR a format scored
//             but its reader threw. Either way there is NO format picker — which
//             format an export is is detection's job, not a survivor's guess,
//             and the small known set is one the machine already covers (a
//             cover-less OFW auto-detects on its message backbone; the cover is
//             a 5% bonus, never required). A blind "pick a format and we'll try
//             again" just re-runs a parse the machine already ran. So recover is
//             an honest handoff: what happened + a get-in-touch CTA + a way to
//             choose a different file. The mailto is a plain, first-person note
//             the user can affirm and send confidently: it names only the KIND
//             of file (PDF, CSV…) and their pilot code — never the filename
//             (which can hold party names) and never any message text.
//
// Copy carries no tech/domain jargon (never "adapter"/"parser"/error-class
// names). The Continue button uses the wizard's --primary token; when disabled
// the shared button rule dims it — readable, not the faint --ink-mute.

import type { FormatChoice, PdfFormat } from "../adapters/_pdf_ingest";
import { buildIntakeMailto } from "../lib/intake";

export interface FormatConfirmStepProps {
  mode: "confirm" | "recover";
  filename: string;
  /** Confirm-only — the formats to pick from. Recover has no picker. */
  choices?: FormatChoice[];
  selected: PdfFormat | null;
  onSelect: (format: PdfFormat) => void;
  onContinue: (format: PdfFormat) => void;
  onChooseDifferent: () => void;
  /**
   * Recover-only: why we're here, in plain language. When a *detected* format
   * was tried and its reader threw, this carries the reason (e.g. "Aurora
   * recognized this as OurFamilyWizard but couldn't read it"); absent for a
   * nothing-recognized recover, where a generic lede is used.
   */
  note?: string;
  /**
   * The pilot access code the user logged in with. Embedded as a visible body
   * line in the recover-state get-in-touch email so the note can be tied to
   * their case. Optional (the standalone dev build may have none).
   */
  accessCode?: string;
}

// A plain-English noun for the file the user dropped, derived from its
// extension. Used in the get-in-touch email so the note is concrete — "a PDF"
// rather than "my messages" — without ever carrying the filename itself
// (which can hold party names).
function humanFileKind(filename: string): string {
  const ext = filename.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  switch (ext) {
    case "pdf":
      return "a PDF";
    case "csv":
      return "a CSV file";
    case "txt":
    case "text":
      return "a text file";
    case "zip":
      return "a ZIP export";
    default:
      return "a file";
  }
}

export function FormatConfirmStep(props: FormatConfirmStepProps) {
  const { mode, filename, choices, selected, note } = props;

  // ── recover ──────────────────────────────────────────────────────────────
  // No format picker (see header): an honest handoff. The reason leads, the
  // get-in-touch CTA is the primary path, and "choose a different file" is the
  // way out.
  if (mode === "recover") {
    // `note` present (case A) means a format WAS recognized but its reader
    // threw — so don't tell triage it's "a format you don't support yet" (it
    // isn't; it's a reader bug on a format we do support). Branch the lead and
    // subject so the email accurately describes which case it is.
    const helpMailto = buildIntakeMailto({
      subject: note
        ? "Aurora — recognized my messages but couldn't read them"
        : "Aurora — a format you don't read yet",
      lead: note
        ? "Hi,\n\nAurora recognized my messages but couldn't read this file. " +
          "Could you help me get them analyzed?"
        : `Hi,\n\nI tried to bring in ${humanFileKind(filename)} of my ` +
          "messages, but Aurora couldn't read it — it may be in a format you " +
          "don't support yet. Could you help me get them analyzed?",
      code: props.accessCode,
    });
    const reason = note ?? "It doesn't match any format Aurora reads yet.";
    return (
      <div className="format-confirm">
        <h1 className="step-title">We couldn't read this file.</h1>
        <p className="step-lede">{reason}</p>

        {/* FILE block — flat row, so the user knows which file this is about. */}
        <div className="format-confirm-field">
          <div className="format-confirm-label">File</div>
          <div className="format-confirm-filename">{filename}</div>
        </div>

        {/* The primary path out — reach a human, not a guess that won't work. */}
        <div className="format-confirm-help format-confirm-help-primary">
          <p className="format-confirm-help-text">
            That's not a dead end — tell us what you have and we'll help you get
            it analyzed.
          </p>
          <a className="format-confirm-help-link" href={helpMailto}>
            Tell us what you have →
          </a>
        </div>

        <div className="wiz-cta">
          <button
            type="button"
            className="ghost"
            onClick={props.onChooseDifferent}
          >
            ← Choose a different file
          </button>
        </div>
      </div>
    );
  }

  // ── confirm ──────────────────────────────────────────────────────────────
  // Detection has a guess but wasn't sure. Show it (tagged) with alternatives;
  // the user confirms or overrides, then Continue parses with that format.
  return (
    <div className="format-confirm">
      <h1 className="step-title">Confirm the format.</h1>
      <p className="step-lede">
        Aurora's detection wasn't confident. Confirm the format, or pick another.
      </p>

      {/* FILE block — flat row, divider above the FORMAT section (no card). */}
      <div className="format-confirm-field">
        <div className="format-confirm-label">File</div>
        <div className="format-confirm-filename">{filename}</div>
      </div>

      {/* FORMAT section — the only bordered elements are the selectable
          options (form fields get borders; nothing else does). */}
      <div className="format-confirm-field format-confirm-field-divided">
        <div className="format-confirm-label">Format</div>
        <div className="format-confirm-options" role="radiogroup" aria-label="Format">
          {(choices ?? []).map((c) => {
            const isSelected = selected === c.format;
            return (
              <button
                key={c.format}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={`format-confirm-option${isSelected ? " selected" : ""}${
                  c.isGuess ? " guess" : ""
                }`}
                onClick={() => props.onSelect(c.format)}
              >
                <span className="format-confirm-option-name">{c.label}</span>
                {c.isGuess && (
                  <span className="format-confirm-option-tag">Aurora's guess</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="wiz-cta">
        <button type="button" className="ghost" onClick={props.onChooseDifferent}>
          ← Choose a different file
        </button>
        <button
          type="button"
          className="primary"
          disabled={selected == null}
          onClick={() => selected != null && props.onContinue(selected)}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
